import { useProctoredAttempt } from '../../components/focus-guard/FocusGuardContext';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { 
  BookOpen, Clock, Play, CheckCircle, ChevronRight, ChevronLeft,
  FileText, Video, HelpCircle, Award, ArrowLeft, X, Check,
  AlertCircle, RotateCcw, Loader2
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { LinkPlayer } from '../../components/media/LinkPlayerDialog';

const TechLearningLesson = () => {
  const { lessonId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [lesson, setLesson] = useState(null);
  const [progress, setProgress] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [navigation, setNavigation] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Quiz state
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  useProctoredAttempt({ active: lesson?.type === 'quiz' && !quizResult, label: 'quiz' });
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  
  // Video state
  const [videoProgress, setVideoProgress] = useState(0);
  
  // Text state
  const [readComplete, setReadComplete] = useState(false);

  const fetchLesson = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/tech-learning/lessons/${lessonId}`);
      setLesson(response.data.lesson);
      setProgress(response.data.progress);
      setEnrollment(response.data.enrollment);
      setNavigation(response.data.navigation);
      
      // Reset quiz state for new lesson
      setQuizAnswers({});
      setQuizResult(null);
      
      // Set initial states based on progress
      if (response.data.progress) {
        setVideoProgress(response.data.progress.video_progress_seconds || 0);
        setReadComplete(response.data.progress.read_completed || false);
      }
    } catch (error) {
      console.error('Failed to fetch lesson:', error);
    } finally {
      setLoading(false);
    }
  }, [api, lessonId]);

  useEffect(() => {
    fetchLesson();
  }, [fetchLesson]);

  const updateProgress = async (updates) => {
    try {
      await api.post(`/tech-learning/lessons/${lessonId}/progress`, updates);
    } catch (error) {
      console.error('Failed to update progress:', error);
    }
  };

  const handleVideoComplete = () => {
    updateProgress({ video_completed: true });
    fetchLesson();
  };

  const handleTextComplete = () => {
    setReadComplete(true);
    updateProgress({ read_completed: true });
    fetchLesson();
  };

  const handleQuizSubmit = async () => {
    if (!lesson?.questions) return;
    
    // Check if all questions are answered
    const unanswered = lesson.questions.filter(q => !quizAnswers[q.id]);
    if (unanswered.length > 0) {
      alert(`Please answer all questions. ${unanswered.length} remaining.`);
      return;
    }
    
    try {
      setSubmittingQuiz(true);
      const response = await api.post(`/tech-learning/lessons/${lessonId}/quiz/submit`, {
        lesson_id: lessonId,
        answers: quizAnswers
      });
      setQuizResult(response.data);
      fetchLesson();
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      alert(error.response?.data?.detail || 'Failed to submit quiz');
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const handleRetryQuiz = () => {
    setQuizAnswers({});
    setQuizResult(null);
  };

  const goToNextLesson = () => {
    if (navigation?.next_lesson) {
      navigate(`/tech-learning/lesson/${navigation.next_lesson.id}`);
    } else {
      navigate(`/tech-learning/course/${enrollment?.course_id}`);
    }
  };

  const goToPrevLesson = () => {
    if (navigation?.prev_lesson) {
      navigate(`/tech-learning/lesson/${navigation.prev_lesson.id}`);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  if (!lesson) {
    return (
      <MainLayout>
        <Card className="p-12 text-center">
          <AlertCircle className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
          <h2 className="text-2xl font-bold mt-4">Lesson Not Found</h2>
          <Button className="mt-4" onClick={() => navigate('/tech-learning/my-courses')}>
            Back to My Courses
          </Button>
        </Card>
      </MainLayout>
    );
  }

  const isComplete = progress?.status === 'completed';

  // Render Video Lesson
  const renderVideoLesson = () => (
    <div className="space-y-4">
      {lesson.video_url ? (
        <LinkPlayer url={lesson.video_url} title={lesson.title} kind="video" />
      ) : (
        <div className="aspect-video bg-black rounded-lg overflow-hidden">
          <div className="w-full h-full flex items-center justify-center text-white">
            <Video className="h-16 w-16 opacity-50" />
          </div>
        </div>
      )}
      
      {!isComplete && (
        <div className="flex justify-center">
          <Button onClick={handleVideoComplete} size="lg">
            <CheckCircle className="h-5 w-5 mr-2" />
            Mark as Complete
          </Button>
        </div>
      )}
      
      {lesson.transcript && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Transcript</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{lesson.transcript}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Render Text Lesson
  const renderTextLesson = () => (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <ScrollArea className="h-[500px] pr-4">
            <div className="prose prose-slate dark:prose-invert max-w-none">
              <ReactMarkdown>{lesson.content || ''}</ReactMarkdown>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
      
      {!isComplete && (
        <div className="flex justify-center">
          <Button onClick={handleTextComplete} size="lg">
            <CheckCircle className="h-5 w-5 mr-2" />
            Mark as Complete
          </Button>
        </div>
      )}
    </div>
  );

  // Render Quiz Lesson
  const renderQuizLesson = () => (
    <div className="space-y-6">
      {quizResult ? (
        // Quiz Results
        <div className="space-y-6">
          <Card className={quizResult.result.passed ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className={quizResult.result.passed ? 'text-green-700' : 'text-red-700'}>
                    {quizResult.result.passed ? 'Quiz Passed!' : 'Quiz Not Passed'}
                  </CardTitle>
                  <CardDescription>
                    Attempt {quizResult.result.attempt_number} • {quizResult.attempts_remaining} attempts remaining
                  </CardDescription>
                </div>
                <div className="text-center">
                  <div className={`text-4xl font-bold ${quizResult.result.passed ? 'text-green-600' : 'text-red-600'}`}>
                    {quizResult.result.percentage}%
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {quizResult.result.correct_answers}/{quizResult.result.total_questions} correct
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Progress 
                value={quizResult.result.percentage} 
                className={`h-3 ${quizResult.result.passed ? '[&>div]:bg-green-500' : '[&>div]:bg-red-500'}`}
              />
              <p className="text-sm text-muted-foreground mt-2">
                Passing score: {quizResult.passing_score}%
              </p>
            </CardContent>
          </Card>

          {/* Feedback */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Review Answers</h3>
            {quizResult.result.feedback.map((fb, index) => {
              const question = lesson.questions?.find(q => q.id === fb.question_id);
              return (
                <Card key={fb.question_id} className={fb.correct ? 'border-green-200' : 'border-red-200'}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start gap-3">
                      <div className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${
                        fb.correct ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                      }`}>
                        {fb.correct ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                      </div>
                      <div>
                        <CardTitle className="text-base">
                          Q{index + 1}: {question?.question}
                        </CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {fb.explanation && (
                      <Alert className="mt-2">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Explanation</AlertTitle>
                        <AlertDescription>{fb.explanation}</AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-4">
            {!quizResult.result.passed && quizResult.attempts_remaining > 0 && (
              <Button variant="outline" onClick={handleRetryQuiz}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Retry Quiz
              </Button>
            )}
            <Button onClick={goToNextLesson}>
              {navigation?.next_lesson ? 'Next Lesson' : 'Back to Course'}
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      ) : (
        // Quiz Questions
        <div className="space-y-6">
          <Alert>
            <HelpCircle className="h-4 w-4" />
            <AlertTitle>Quiz Instructions</AlertTitle>
            <AlertDescription>
              Answer all questions and submit. You need {lesson.passing_score || 70}% to pass.
              You have {lesson.max_attempts || 3} attempts.
            </AlertDescription>
          </Alert>

          {lesson.questions?.map((question, qIndex) => (
            <Card key={question.id} data-testid={`quiz-question-${question.id}`}>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="flex items-center justify-center h-6 w-6 rounded-full bg-primary/10 text-primary text-sm">
                    {qIndex + 1}
                  </span>
                  {question.question}
                </CardTitle>
                <CardDescription>{question.points} points</CardDescription>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={quizAnswers[question.id] || ''}
                  onValueChange={(value) => setQuizAnswers(prev => ({
                    ...prev,
                    [question.id]: value
                  }))}
                >
                  {question.options?.map((option) => (
                    <div 
                      key={option.id} 
                      className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
                    >
                      <RadioGroupItem value={option.id} id={`${question.id}-${option.id}`} />
                      <Label 
                        htmlFor={`${question.id}-${option.id}`}
                        className="flex-1 cursor-pointer"
                      >
                        {option.text}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          ))}

          <div className="flex justify-center">
            <Button 
              size="lg" 
              onClick={handleQuizSubmit}
              disabled={submittingQuiz || Object.keys(quizAnswers).length !== lesson.questions?.length}
            >
              {submittingQuiz ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Submit Quiz
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="lesson-view">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/tech-learning/course/${enrollment?.course_id}`)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Course
          </Button>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Lesson {navigation?.current_index + 1} of {navigation?.total_lessons}</span>
            {isComplete && (
              <Badge className="bg-green-100 text-green-700">
                <CheckCircle className="h-3 w-3 mr-1" />
                Completed
              </Badge>
            )}
          </div>
        </div>

        {/* Lesson Header */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline">
              {lesson.type === 'video' && <Video className="h-3 w-3 mr-1" />}
              {lesson.type === 'text' && <FileText className="h-3 w-3 mr-1" />}
              {lesson.type === 'quiz' && <HelpCircle className="h-3 w-3 mr-1" />}
              {lesson.type}
            </Badge>
            <span className="text-sm text-muted-foreground">
              <Clock className="h-3 w-3 inline mr-1" />
              {lesson.duration_minutes} min
            </span>
          </div>
          <h1 className="text-2xl font-bold">{lesson.title}</h1>
          {lesson.description && (
            <p className="text-muted-foreground mt-1">{lesson.description}</p>
          )}
        </div>

        {/* Lesson Content */}
        <div className="min-h-[400px]">
          {lesson.type === 'video' && renderVideoLesson()}
          {lesson.type === 'text' && renderTextLesson()}
          {lesson.type === 'quiz' && renderQuizLesson()}
        </div>

        {/* Navigation */}
        {lesson.type !== 'quiz' && (
          <div className="flex items-center justify-between pt-6 border-t">
            <Button
              variant="outline"
              onClick={goToPrevLesson}
              disabled={!navigation?.prev_lesson}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>
            
            <Button onClick={goToNextLesson}>
              {navigation?.next_lesson ? (
                <>
                  Next Lesson
                  <ChevronRight className="h-4 w-4 ml-2" />
                </>
              ) : (
                <>
                  Finish Course
                  <Award className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default TechLearningLesson;
